import { Button } from '@caterinx/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@caterinx/ui/components/ui/card';
import { Input } from '@caterinx/ui/components/ui/input';
import { Label } from '@caterinx/ui/components/ui/label';
import { Wordmark } from '@caterinx/ui/components/ui/wordmark';
import { useToast } from '@caterinx/ui/hooks/use-toast';
import { isAxiosError } from 'axios';
import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../../lib/api.js';
import { authQuery, safeRedirect } from '../../lib/redirect.js';

export function VerifyEmailPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Der Anmeldeweg trägt das Ziel durch (Einladung per E-Mail, D32) — nur In-App-Pfade.
  const redirect = safeRedirect(searchParams.get('redirect'));

  const handleSubmit = async (formEvent: FormEvent): Promise<void> => {
    formEvent.preventDefault();
    setIsSubmitting(true);

    try {
      await api.post('/auth/verify-email', { email, code });
      toast({ title: 'E-Mail bestätigt', description: 'Sie können sich jetzt anmelden.' });
      navigate(redirect ? `/login?${authQuery({ email, redirect })}` : '/login', { replace: true });
    } catch (error) {
      const data = isAxiosError(error) ? (error.response?.data as { message?: string }) : undefined;
      toast({
        variant: 'destructive',
        title: 'Bestätigung fehlgeschlagen',
        description: data?.message ?? 'Bitte prüfen Sie den Code.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async (): Promise<void> => {
    try {
      await api.post('/auth/verify-email', { email, resend: true });
      toast({ title: 'Code erneut versendet' });
    } catch {
      toast({ variant: 'destructive', title: 'Versand fehlgeschlagen' });
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-8 p-4">
      <Wordmark className="w-44" />
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>E-Mail bestätigen</CardTitle>
          <CardDescription>Bitte geben Sie den Code aus unserer E-Mail ein.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(changeEvent) => setEmail(changeEvent.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="code">Bestätigungscode</Label>
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(changeEvent) => setCode(changeEvent.target.value)}
              />
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Wird geprüft...' : 'Bestätigen'}
            </Button>
          </form>

          <Button variant="ghost" className="mt-4 w-full" onClick={handleResend} disabled={!email}>
            Code erneut senden
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
